import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import { Grid, Typography, useMediaQuery } from '@mui/material';
import { ResultsCard, TournamentPartnerCard } from 'components/Cards';
import { get, getUserIdFromToken } from 'api';
import Button from 'components/Button';
import CustomPagination from 'components/Pagination';
import CryptoJS from 'crypto-js';
import { Levels, PadelLevels } from 'constants/Selections';

const MyTournamentMatches = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id } = useParams();
  const [page, setPage] = useState(1);
  const [upcomingMatches, setUpcomingMatches] = useState([]);
  const [pastMatches, setPastMatches] = useState([]);
  const [hideButtons, setHideButtons] = useState(false);
  const [isGroupStage, setIsGroupStage] = useState(false);
  const [isPagrindasStage, setIsPagrindasStage] = useState(false);
  const [isPaguodaStage, setIsPaguodaStage] = useState(false);
  const [myActiveTournamentRegistrations, setMyActiveTournamentRegistrations] =
    useState([]);
  const [tournamentLevels, setTournamentLevels] = useState([]);
  const isMd = useMediaQuery(theme.breakpoints.up('md'), {
    defaultMatches: true,
  });
  const [loading, setLoading] = useState(true);

  const itemsPerPage = isMd ? 6 : 3;

  const handlePaginationChange = (event, value) => {
    setPage(value);
  };

  const isTeam1 = (item) => {
    const userId = id ? parseInt(id) : getUserIdFromToken();
    return item.team1_player1_id === userId || item.team1_player2_id === userId;
  };

  const fetchActiveTournamentRegistration = async (userId) => {
    try {
      const response = await get(
        '/tournament-registration/my-active-tournament-registrations',
      );
      const data = await response.json();
      setMyActiveTournamentRegistrations(data);
    } catch (error) {
      console.error('Failed to fetch active tournament registrations:', error);
    }
  };

  const fetchUpcomingMatches = async (userId) => {
    try {
      const endpoint = userId
        ? `/tournament-matches/user-active-tournament-matches/${userId}`
        : '/tournament-matches/my-active-tournament-matches';
      const response = await get(endpoint);
      const data = await response.json();
      const encryptedData = data.data;
      const bytes = CryptoJS.AES.decrypt(
        encryptedData,
        process.env.REACT_APP_CRYPTO_SECRET,
      );
      const decryptedData = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
      const groupStage = decryptedData.filter(
        (match) => match.stage === 'group',
      );
      const pagrindasStage = decryptedData.filter(
        (match) => match.stage === 'playoffs',
      );
      const paguodaStage = decryptedData.filter(
        (match) => match.stage === 'losers',
      );
      const hideWaitingMatches = decryptedData.filter(
        (match) => match.challenger_id !== 0 && match.opponent_id !== 0,
      );
      setIsGroupStage(groupStage.length > 1);
      setIsPagrindasStage(pagrindasStage.length > 1);
      setIsPaguodaStage(paguodaStage.length > 1);
      const filteredLevels = decryptedData
        .filter((match) => {
          return PadelLevels.includes(match.level_name);
        })
        .map((match) => ({
          level_name: match.level_name,
          type_name: match.type_name,
          tournament_id: match.tournament_id,
          stage: match.stage,
          city: match.city,
          tournament_name: match.tournament_name,
        }));

      const distinctFilteredLevels = filteredLevels.filter(
        (level, index, self) =>
          index ===
          self.findIndex(
            (l) =>
              l.level_name === level.level_name &&
              l.type_name === level.type_name &&
              l.tournament_id === level.tournament_id &&
              l.stage === level.stage &&
              l.city === level.city &&
              l.tournament_name === level.tournament_name,
          ),
      );
      setTournamentLevels(distinctFilteredLevels);
      setUpcomingMatches(decryptedData);
    } catch (error) {
      console.error('Failed to fetch matches:', error);
    }
  };

  const fetchPastMatches = async (userId) => {
    try {
      const endpoint = userId
        ? `/tournament-matches/user-past-tournament-matches/${userId}`
        : '/tournament-matches/my-past-tournament-matches';
      const response = await get(endpoint);
      const data = await response.json();
      const encryptedData = data.data;
      const bytes = CryptoJS.AES.decrypt(
        encryptedData,
        process.env.REACT_APP_CRYPTO_SECRET,
      );
      const playedMatches = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
      setPastMatches(playedMatches);
    } catch (error) {
      console.error('Failed to fetch matches:', error);
    }
  };

  useEffect(() => {
    if (id) {
      setHideButtons(true);
      Promise.all([fetchPastMatches(id), fetchUpcomingMatches(id)]).then(() =>
        setLoading(false),
      );
    } else {
      setHideButtons(false);
      fetchActiveTournamentRegistration();
      Promise.all([fetchPastMatches(null), fetchUpcomingMatches(null)]).then(
        () => setLoading(false),
      );
    }
  }, [id]);

  if (loading) {
    return <div>Loading...</div>;
  }

  const paginatedMatches = pastMatches.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage,
  );

  return (
    <Box>
      <Box marginBottom={'48px'}>
        <Box>
          {upcomingMatches.length > 0 && !hideButtons && (
            <>
              <Typography
                variant={'h4'}
                fontWeight={700}
                color={theme.palette.darkGrey[700]}
                marginBottom={'32px'}
              >
                {t('myTournamentBrackets')}
              </Typography>
              <Box
                marginBottom={'32px'}
                display={'flex'}
                flexDirection={{ xs: 'column', sm: 'row' }}
              >
                {tournamentLevels.map((level, index) => {
                  return (
                    <Button
                      key={index}
                      buttontype="white"
                      bordered="true"
                      width={{ xs: '100%', sm: 'auto' }}
                      marginRight={{ xs: '0px', sm: '32px' }}
                      marginBottom={{ xs: '20px', sm: 0 }}
                      onClick={() =>
                        navigate(`/turnyras/${level.tournament_id}`, {
                          state: {
                            tournament_stage: level.stage,
                            tournament_level: level.level_name,
                            tournament_gender: level.type_name,
                          },
                        })
                      }
                    >
                      <Typography
                        variant={'body3'}
                        color={theme.palette.darkGrey[600]}
                        fontWeight={600}
                      >
                        {t('tournamentBrackets')} - {level.tournament_level} (
                        {level.tournament_name} {level.city})
                      </Typography>
                    </Button>
                  );
                })}
              </Box>
            </>
          )}
        </Box>
        <Typography
          variant={'h4'}
          fontWeight={700}
          color={theme.palette.darkGrey[700]}
          marginBottom={'32px'}
        >
          {t('upcomingTournamentMatches')}
        </Typography>
        <Box display={'flex'} flexDirection={{ xs: 'column', sm: 'row' }}>
          {isGroupStage && !hideButtons && (
            <Box
              marginBottom={{ xs: '20px', sm: '32px' }}
              marginRight={{ sm: '32px' }}
            >
              <Button
                buttontype="white"
                bordered="true"
                width={{ xs: '100%', sm: 'auto' }}
                onClick={() =>
                  navigate('/siusti-zinute', {
                    state: {
                      isTournament: true,
                      isMulti: true,
                      upcomingMatches,
                    },
                  })
                }
              >
                <Typography
                  variant={'body3'}
                  color={theme.palette.darkGrey[600]}
                  fontWeight={600}
                >
                  {t('sendBulkMessage')}
                </Typography>
              </Button>
            </Box>
          )}
        </Box>
        {upcomingMatches.length === 0 &&
        myActiveTournamentRegistrations.length === 0 ? (
          <Box
            sx={{
              maxWidth: { xs: '100%', sm: '464px', md: '416px' },
              width: '100%',
            }}
          >
            <Typography variant={'body3'} color={theme.palette.darkGrey[600]}>
              {t('noUpcomingTournamentMatches')}
            </Typography>
          </Box>
        ) : (
          <>
            {myActiveTournamentRegistrations.length > 0 && (
              <Box
                display={'flex'}
                flexDirection={'column'}
                marginBottom={'32px'}
              >
                {myActiveTournamentRegistrations.map((item, i) => {
                  return (
                    <Box
                      key={i}
                      sx={{
                        width: '100%',
                      }}
                    >
                      <Typography
                        variant={'body3'}
                        color={theme.palette.darkGrey[600]}
                      >
                        {t('registeredTo')}
                        {': '}
                      </Typography>
                      <Typography
                        variant={'body3'}
                        color={theme.palette.darkGrey[600]}
                        fontWeight={600}
                      >
                        {item.tournament_name} ({item.city}) -{' '}
                        {t(`${item.type_name}`)} {item.level_name}{' '}
                      </Typography>
                      <Typography
                        variant={'body3'}
                        color={theme.palette.darkGrey[600]}
                      >
                        {t('levelTournament')}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            )}
            <Grid container spacing={'32px'}>
              {upcomingMatches.map((item, i) => {
                const isT1 = isTeam1(item);
                const team = isT1 ? 'team2' : 'team1';
                return (
                  <Grid item xs={12} sm={6} md={5} key={i}>
                    <TournamentPartnerCard
                      matchId={item.match_id}
                      avatar={item[`${team}_player1_image`]}
                      avatar2={item[`${team}_player2_image`]}
                      name={item[`${team}_player1`]}
                      name2={item[`${team}_player2`]}
                      level={item.level_name}
                      playUntil={item.play_until}
                      location={item.city}
                      tournament={item.tournament_name}
                      phoneNumber={item[`${team}_player1_phone`]}
                      phoneNumber2={item[`${team}_player2_phone`]}
                      userId={item[`${team}_player1_id`]}
                      userId2={item[`${team}_player2_id`]}
                      mailTo={item[`${team}_player1_email`]}
                      mailTo2={item[`${team}_player2_email`]}
                      hideButtons={hideButtons}
                      matchData={item}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </>
        )}
      </Box>
      <Box>
        <Typography
          variant={'h4'}
          fontWeight={700}
          color={theme.palette.darkGrey[700]}
          marginBottom={'32px'}
        >
          {t('pastTournamentMatches')}
        </Typography>
        {pastMatches.length === 0 ? (
          <Box
            sx={{
              maxWidth: { xs: '100%', sm: '464px', md: '416px' },
              width: '100%',
            }}
          >
            <Typography variant={'body3'} color={theme.palette.darkGrey[600]}>
              {t('noPastTournamentMatches')}
            </Typography>
          </Box>
        ) : (
          <>
            <Grid container spacing={'32px'}>
              {pastMatches.length > 0 &&
                paginatedMatches.map((item, i) => {
                  return (
                    <Grid item xs={12} sm={4} key={i}>
                      <ResultsCard data={item} page={'tournaments'} />
                    </Grid>
                  );
                })}
            </Grid>
            <Box
              marginTop="20px"
              paddingTop="20px"
              maxWidth="100%"
              display="flex"
              justifyContent="center"
              borderTop={`1px solid ${theme.palette.darkGrey[200]}`}
            >
              <CustomPagination
                page={page}
                handlePaginationChange={handlePaginationChange}
                count={Math.ceil(pastMatches.length / itemsPerPage)}
              />
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default MyTournamentMatches;
